import React, { useEffect, useRef, useState, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

export interface EditableTextProps {
  value: string;
  onChange: (newValue: string) => void;
  as?: React.ElementType;
  placeholder?: string;
  placeholderKey?: string;
  multiline?: boolean;
  ariaLabel: string;
  className?: string;
  inputClassName?: string;
  containerClassName?: string;
  disabled?: boolean;
  maxLength?: number;
  showCharacterCount?: boolean;
}

export const EditableText = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, EditableTextProps>(
  (
    {
      as: Component = 'p',
      value,
      onChange,
      placeholder,
      placeholderKey,
      multiline = false,
      ariaLabel,
      className,
      inputClassName,
      containerClassName,
      disabled = false,
      maxLength,
      showCharacterCount = false,
    },
    ref
  ) => {
    const { t } = useTranslation();

    const [isEditing, setIsEditing] = useState(false);
    const [justFinishedEditing, setJustFinishedEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState<string>(value);
    const [lastCommittedValue, setLastCommittedValue] = useState<string>(value);
    const lastPropValueRef = useRef<string>(value);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    const resolvedPlaceholder = placeholderKey ? t(placeholderKey) : placeholder;

    // Sync from parent only when prop actually changes and we're not editing.
    useEffect(() => {
      if (value !== lastPropValueRef.current) {
        lastPropValueRef.current = value;
        if (!isEditing) {
          setLastCommittedValue(value);
          setCurrentValue(value);
        }
      }
    }, [value, isEditing]);

    // Entering edit mode: focus and place caret at end; keep wrapper visuals identical to hover.
    useEffect(() => {
      if (isEditing && !disabled) {
        setCurrentValue(lastCommittedValue);

        const node = inputRef.current;
        if (!node) return;

        node.focus();

        if (multiline && node instanceof HTMLTextAreaElement) {
          node.style.height = 'auto';
          node.style.height = `${node.scrollHeight}px`;
        }

        const len = (node.value ?? '').length;
        requestAnimationFrame(() => {
          try {
            node.setSelectionRange(len, len);
          } catch {
            // ignore if unsupported
          }
        });
      }
    }, [isEditing, disabled, multiline, lastCommittedValue]);

    const setRefs = (node: HTMLInputElement | HTMLTextAreaElement | null) => {
      (inputRef as React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>).current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>).current = node;
      }
    };

    const commitAndClose = () => {
      const trimmed = currentValue.trim();
      setIsEditing(false);
      setJustFinishedEditing(true);
      setLastCommittedValue(trimmed); // keep it visible even if parent is slow to update
      if (trimmed !== value) onChange(trimmed);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'Escape') {
        setCurrentValue(lastCommittedValue);
        setIsEditing(false);
      } else if (event.key === 'Enter') {
        if (!multiline || (multiline && !event.shiftKey)) {
          event.preventDefault();
          commitAndClose();
        }
      }
    };

    const handleWrapperKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (!isEditing && (event.key === 'Enter' || event.key === ' ' || event.key === 'F2')) {
        event.preventDefault();
        setIsEditing(true);
      }
    };

    const handleWrapperClick = () => {
      if (!disabled && !isEditing) {
        setIsEditing(true);
        setJustFinishedEditing(false);
      }
    };

    const handleMouseMove = () => {
      if (justFinishedEditing) {
        setJustFinishedEditing(false);
      }
    };

    // Input/textarea gets no border/outline/ring; wrapper owns visuals.
    const commonInputProps = {
      ref: setRefs,
      value: currentValue,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentValue(e.target.value),
      onBlur: commitAndClose,
      onKeyDown: handleKeyDown,
      placeholder: resolvedPlaceholder,
      'aria-label': ariaLabel,
      maxLength,
      disabled,
      className: clsx(
        'w-full bg-transparent text-slate-900 placeholder:text-slate-400',
        '[.theme-liquid-glass_&]:text-white [.theme-liquid-glass_&]:placeholder:text-white/40',
        // Remove any native browser focus visuals completely (fixes blue highlight)
        'border-none outline-none ring-0 focus:border-none focus:outline-none focus:ring-0 focus-visible:outline-none',
        // Avoid Safari/iOS native styles
        'appearance-none',
        // Typography comes from outer className
        className,
        inputClassName
      ),
      style: multiline ? { resize: 'none' as const } : undefined,
    };

    // Persistent wrapper controls border/transform/shadow so hover == active(editing).
    const wrapperClass = clsx(
      'block w-full rounded-lg -m-2 p-2 border-2 transition-all duration-150',
      // Default border hidden to avoid layout shift; show on hover/focus/active
      'border-transparent',
      !disabled && 'cursor-text',
      // Hover: 1px lift + black border + shadow (Subtle "Jiggle" variant)
      !disabled && !justFinishedEditing &&
        'hover:-translate-x-px hover:-translate-y-px hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)] hover:border-black',
      // Focus (keyboard) should look the same as hover, not blue/green rings
      !disabled &&
        'focus:-translate-x-px focus:-translate-y-px focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)] focus:border-black',
      // Active (mouse down) should also show the same visuals immediately
      !disabled &&
        'active:-translate-x-px active:-translate-y-px active:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)] active:border-black',
      // When editing, lock in the exact hover visuals so nothing moves
      isEditing && '!border-black !-translate-x-px !-translate-y-px !shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)]',
      '[.theme-liquid-glass_&]:border-white/20',
      '[.theme-liquid-glass_&]:hover:border-white/30',
      !disabled && '[.theme-liquid-glass_&]:hover:translate-x-0 [.theme-liquid-glass_&]:hover:translate-y-0 [.theme-liquid-glass_&]:hover:shadow-none',
      !disabled && '[.theme-liquid-glass_&]:focus:translate-x-0 [.theme-liquid-glass_&]:focus:translate-y-0 [.theme-liquid-glass_&]:focus:shadow-[0_0_0_2px_rgba(139,92,246,0.3)]',
      !disabled && '[.theme-liquid-glass_&]:active:translate-x-0 [.theme-liquid-glass_&]:active:translate-y-0 [.theme-liquid-glass_&]:active:shadow-none',
      isEditing && '[.theme-liquid-glass_&]:!border-white/30 [.theme-liquid-glass_&]:!translate-x-0 [.theme-liquid-glass_&]:!translate-y-0 [.theme-liquid-glass_&]:!shadow-none',
      // Remove any outline artifacts
      'focus:outline-none focus-visible:outline-none',
      disabled && 'cursor-not-allowed opacity-70 shadow-none translate-x-0 translate-y-0',
      containerClassName
    );

    const displayValue = lastCommittedValue || resolvedPlaceholder || '';
    const showPlaceholderStyle = !lastCommittedValue;

    return (
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={handleWrapperClick}
        onKeyDown={handleWrapperKeyDown}
        onMouseMove={handleMouseMove}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        className={wrapperClass}
      >
        {isEditing ? (
          multiline ? (
            <>
              <textarea
                {...commonInputProps}
                rows={1}
                onInput={(e) => {
                  const target = e.currentTarget;
                  target.style.height = 'auto';
                  target.style.height = `${target.scrollHeight}px`;
                }}
              />
              {showCharacterCount && (
                <div className="mt-1.5 flex justify-end text-xs text-slate-500 [.theme-liquid-glass_&]:text-white/60">
                  {String(currentValue ?? '').length}
                  {maxLength !== undefined && ` / ${maxLength}`}
                </div>
              )}
            </>
          ) : (
            <>
              <input type="text" {...commonInputProps} />
              {showCharacterCount && (
                <div className="mt-1.5 flex justify-end text-xs text-slate-500 [.theme-liquid-glass_&]:text-white/60">
                  {String(currentValue ?? '').length}
                  {maxLength !== undefined && ` / ${maxLength}`}
                </div>
              )}
            </>
          )
        ) : (
          <Component
            className={clsx(
              'w-full',
              showPlaceholderStyle && 'text-slate-400 italic [.theme-liquid-glass_&]:text-white/40',
              className
            )}
          >
            {displayValue}
          </Component>
        )}
      </div>
    );
  }
);

EditableText.displayName = 'EditableText';
